import React, { createContext, useState, useEffect } from 'react';

/**
 * Theme Context for managing light/dark mode
 * 
 * This context provides theme state and toggle functionality throughout the application,
 * allowing components to access and change the current theme.
 * 
 * Features:
 * - Persists theme preference in localStorage
 * - Detects and respects user's system preference
 * - Provides a toggle function to switch between themes
 * - Automatically applies theme classes to the document
 * 
 * Usage:
 * 1. Wrap your application with ThemeProvider
 * 2. Use the useTheme hook to access theme state and toggle function
 */
export const ThemeContext = createContext();

/**
 * Theme Provider Component
 * 
 * Wraps the application to provide theme context to all child components.
 * Manages theme state and handles theme changes.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const ThemeProvider = ({ children }) => {
  // Determine the initial theme based on localStorage or system preference
  const getInitialTheme = () => {
    // First check if theme is stored in localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme;
    }
    
    // If no saved preference, check system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  };

  // Initialize theme state with the determined initial theme
  const [theme, setTheme] = useState(getInitialTheme);

  /**
   * Toggle between light and dark themes
   * Updates state and persists the new theme to localStorage
   */
  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme);
      return newTheme;
    });
  };

  // Apply theme class to document when theme changes
  useEffect(() => {
    const root = window.document.documentElement;
    
    // Remove both theme classes to ensure clean state
    root.classList.remove('light', 'dark');
    
    // Add the current theme class
    root.classList.add(theme);
    
    // Store theme preference in localStorage for persistence
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Provide theme state and toggle function to children
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext; 